import { FC, useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { connect } from "react-redux";
import { FileDown } from "lucide-react";
import { downloadExcel } from "react-export-table-to-excel";
import { AppDispatch, RootState } from "../../store";
import { MoreOptions, Table } from "../../components";
import useWindowBreakpoints from "../../hooks/useWindowBreakpoints";
import {
  fetchUserDetails,
  openAccessRequestModal,
  sendRequest,
  updateAccess,
} from "../../store/modules/auth";
import DefaultHeader from "../../components/DefaultHeader";
import { getUserDetailColumns } from "./utils";
import {
  User,
  UserDetail,
  UpdateAccessPayload,
  AccessRequestPayload,
} from "../../types/auth.types";
import "./styles.css";
import ProvideAccessModal from "./components/ProvideAccessModal/ProvideAccessModal";
import { Button } from "@albertsons/uds/molecule/Button";

interface AccessControlPageProps {
  forceScreen?: "extraSmall" | "mobile" | "desktop";
  user: User | null;
  isOpenAccessRequestModal: boolean;
  isRequestingUserAccess: boolean;
  accessRequestError: string | null;
  isLoadingUserDetails: boolean;
  userDetails: UserDetail[];
  fetchUserDetails: () => void;
  openAccessRequestModal: (value: boolean) => void;
  sendUserRequest: (data: AccessRequestPayload) => void;
  updateUserAccess: (data: UpdateAccessPayload) => void;
}

const AccessControlPage: FC<AccessControlPageProps> = (props) => {
  const {
    forceScreen,
    user,
    isOpenAccessRequestModal,
    accessRequestError,
    isRequestingUserAccess,
    isLoadingUserDetails,
    userDetails,
    fetchUserDetails,
    openAccessRequestModal,
    sendUserRequest,
    updateUserAccess,
  } = props;

  const [showTableOption, setShowTableOption] = useState(false);

  const isInitializedRef = useRef(false);

  const { getScreen } = useWindowBreakpoints();
  const screen = getScreen(forceScreen);

  const userDetailColumns = getUserDetailColumns({
    loggedInUserId: user ? user.id : null,
    updateAccess: updateUserAccess,
  });

  const onExportHandler = () => {
    setShowTableOption(false);

    const header = userDetailColumns.map((column) => column.label);
    const body = userDetails.map((detail) => {
      const row: Array<number | string> = [];
      userDetailColumns.forEach((column) => {
        row.push(detail[column.id as keyof UserDetail]);
      });
      return row;
    });

    downloadExcel({
      fileName: "mView - Usage Metrics",
      sheet: "mView",
      tablePayload: {
        header,
        body,
      },
    });
  };

  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      fetchUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = "Access Control | IMPACT mVIEW Dashboard";
  }, []);

  return (
    <>
      <DefaultHeader
        forceScreen={forceScreen}
        title="Access Control"
        subTitle="Permission Requests"
      />
      <div className="relative">
        {screen.large && (
          <div
            className="absolute top-0 left-0 w-full h-[150px] z-[10]"
            style={{
              background: "linear-gradient(to bottom, #124d8d, white)",
            }}
          />
        )}
        <div
          className={clsx(
            "py-3 relative z-[20]",
            screen.isMobile ? "px-3" : "px-5"
          )}
        >
          <div
            className={clsx(
              "flex justify-end items-center gap-2 mb-4",
              screen.large && "text-white"
            )}
          >
            For providing access to new users
            <Button
              fixed
              className="!w-24"
              onClick={() => openAccessRequestModal(true)}
            >
              Click here
            </Button>
          </div>
          <div className="w-full">
            <MoreOptions
              showOptions={showTableOption}
              setShowOptions={setShowTableOption}
              options={[
                {
                  label: "Export Data",
                  icon: <FileDown size={20} />,
                  onClick: onExportHandler,
                  dataTestId: "export-button",
                },
              ]}
            />
            <Table<UserDetail>
              id="accessControl"
              columns={userDetailColumns}
              rows={[...userDetails]}
              isLoading={isLoadingUserDetails}
              className="access-control-table"
            />
          </div>
        </div>
      </div>
      <ProvideAccessModal
        isOpen={isOpenAccessRequestModal}
        isLoading={isRequestingUserAccess}
        error={accessRequestError}
        onClose={() => openAccessRequestModal(false)}
        provideAccess={sendUserRequest}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOpenAccessRequestModal: state.auth.isOpenAccessRequestModal,
  accessRequestError: state.auth.accessRequestError,
  isRequestingUserAccess: state.auth.isRequestingUserAccess,
  isLoadingUserDetails: state.auth.isLoadingUserDetails,
  user: state.auth.user,
  userDetails: state.auth.userDetails,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchUserDetails: () => dispatch(fetchUserDetails()),
  openAccessRequestModal: (value: boolean) =>
    dispatch(openAccessRequestModal(value)),
  sendUserRequest: (data: AccessRequestPayload) =>
    dispatch(sendRequest(true, data)),
  updateUserAccess: (data: UpdateAccessPayload) => dispatch(updateAccess(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccessControlPage);
